import { Check, Sparkles, Shield, Crown, Target, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// PricingContent — single source of truth for engagement tiers.
//
// Rendered by:
//   1. PitchDeckPage · PricingSlide  → centered bento variant
//   2. PricingPage (/pricing, public) → full-page, print-optimised
//
// OmniDraftLab is an advisory service, not SaaS — the tier shape maps to
// *depth of engagement*, not seat counts.
// ---------------------------------------------------------------------------

export const PRICING_TIERS = [
  {
    key: "evaluation",
    name: "Evaluation",
    tone: "cyan",
    tagline: "The NFL grade, the comp, the verdict.",
    description: "For athletes and families who want to know where he really stands before they spend another dollar on prep.",
    price: "$5K",
    priceRange: "– $10K",
    cadence: "one-time",
    features: [
      "Tape evaluation · all available film",
      "1.0–9.0 NFL grade (General + Position)",
      "Ceiling · Realistic · Floor comparisons",
      "Scheme fit read across 32 teams",
      "Written scouting report (PDF)",
      "1 x 60-min family debrief call",
    ],
  },
  {
    key: "draft_prep",
    name: "Draft Prep",
    tone: "glass",
    tagline: "Evaluation + strategic positioning.",
    description: "For the prospect who's projecting Round 1–3 and needs to be ready for every room he walks into.",
    price: "$25K",
    priceRange: "",
    cadence: "per cycle",
    features: [
      "Everything in Evaluation",
      "Interview prep · 6-dimension AI scoring",
      "Development roadmap · prioritized improvement plan",
      "Combine / Pro-Day playbook + rehearsal",
      "Media training (hostile-Q drills)",
      "Whiteboard & install preparation",
      "Weekly progress digest for family",
    ],
  },
  {
    key: "full_advisory",
    name: "Full Advisory",
    tone: "rose",
    featured: true,
    tagline: "The complete front-office service.",
    description: "For athletes with first-round talent — we act as the operating system for the entire cycle. Every room, every decision.",
    price: "$50K",
    priceRange: "",
    cadence: "per cycle",
    features: [
      "Everything in Draft Prep",
      "Agent vetting + matchmaking (8-dim scorecard)",
      "Character & background review · social audit · reputation guidance",
      "Private Family Portal + live advisory",
      "Decision-matrix for every major fork",
      "Brand, NIL & narrative strategy",
      "Dedicated principal as single point of contact",
      "On-site support · Combine & Pro Day",
    ],
  },
  {
    key: "elite",
    name: "Elite",
    tone: "cream",
    tagline: "White-glove. One client per cycle.",
    description: "For the generational prospect. Full advisory, plus on-ground presence in every city, every week, every meeting.",
    price: "$75K",
    priceRange: "",
    cadence: "per cycle",
    features: [
      "Everything in Full Advisory",
      "On-site presence as needed",
      "24/7 decision-room access",
      "Full post-draft transition support",
      "Lifetime advisory relationship",
    ],
  },
];

const TONE_STYLES = {
  cyan:  { badge: "bg-cyan-300/15 text-cyan-200 ring-cyan-300/30",   accent: "text-cyan-300",   check: "text-cyan-300"   },
  glass: { badge: "bg-white/10 text-1 ring-white/15",                 accent: "text-1",           check: "text-1"          },
  rose:  { badge: "bg-rose-400/15 text-rose-200 ring-rose-400/30",    accent: "text-rose-300",    check: "text-rose-300"    },
  cream: { badge: "bg-amber-200/15 text-amber-100 ring-amber-200/30", accent: "text-amber-200",   check: "text-amber-200"   },
};

/**
 * A single tier card. Compact variant (for slide) drops the long description
 * and the synopsis paragraph to keep the deck readable.
 */
export function PricingTierCard({ tier, compact = false, className = "" }) {
  const tones = TONE_STYLES[tier.tone] || TONE_STYLES.cyan;
  return (
    <div
      data-testid={`pricing-card-${tier.key}`}
      className={cn(
        "relative flex flex-col rounded-2xl bg-white/[0.03] ring-1 ring-white/10 backdrop-blur-md p-6 sm:p-7",
        tier.featured && "ring-2 ring-rose-300/50 bg-gradient-to-b from-rose-500/[0.08] to-transparent",
        className
      )}
    >
      {tier.featured && (
        <div className="absolute -top-3 right-6">
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full bg-rose-400 text-[#1a0306] font-semibold ring-1 ring-rose-300/50 shadow-lg">
            Most Popular
          </span>
        </div>
      )}

      {/* Tier badge */}
      <span className={cn("inline-flex w-fit items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ring-1 font-medium", tones.badge)}>
        {tier.name}
      </span>

      {/* Tagline */}
      <h3 className="mt-4 text-1 text-lg sm:text-xl font-semibold leading-snug">
        {tier.tagline}
      </h3>

      {/* Description */}
      {!compact && (
        <p className="mt-2 text-2 text-sm leading-relaxed">
          {tier.description}
        </p>
      )}

      {/* Price */}
      <div className="mt-5 flex items-baseline flex-wrap gap-2">
        <span className="font-semibold tabular-nums tracking-tighter text-1" style={{ fontSize: "clamp(1.9rem, 3.6vw, 2.75rem)" }}>
          {tier.price}
        </span>
        {tier.priceRange && (
          <span className="font-semibold tabular-nums tracking-tighter opacity-55 text-1" style={{ fontSize: "clamp(1.5rem, 2.8vw, 2.1rem)" }}>
            {tier.priceRange}
          </span>
        )}
        <span className={cn("text-[10px] uppercase tracking-[0.22em] ml-1", tones.accent)}>
          {tier.cadence}
        </span>
      </div>

      {/* Feature list */}
      <div className="mt-5 border-t border-white/5 pt-4 flex-1">
        <ul className="space-y-2">
          {tier.features.map((f, i) => (
            <li key={i} className="text-2 text-sm flex items-start gap-2 leading-snug">
              <Check className={cn("h-3.5 w-3.5 mt-1 shrink-0", tones.check)} strokeWidth={2.5} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>

      {!compact && (
        <a
          href={`mailto:hello@omnidraftlab.com?subject=${encodeURIComponent(`Application · ${tier.name} tier`)}&body=${encodeURIComponent(
            `Hi OmniDraftLab team,\n\nI'd like to apply for the ${tier.name} engagement tier.\n\nAthlete: \nGraduation year: \nPosition / school: \nTimeline / draft class: \n\nBest,\n`
          )}`}
          data-testid={`pricing-apply-${tier.key}`}
          className={cn(
            "mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition ring-1",
            tier.featured
              ? "bg-rose-400 text-[#1a0306] hover:bg-rose-300 ring-rose-300/50"
              : "bg-white/[0.04] text-1 ring-white/15 hover:ring-cyan-300/40 hover:text-cyan-200"
          )}
        >
          Apply with this tier <ArrowRight className="h-3.5 w-3.5" />
        </a>
      )}

      {!compact && (
        <div className="mt-4 text-3 text-[11px] italic">
          {tier.key === "elite" ? "Application-only. One client per class." : "Application-only."}
        </div>
      )}
    </div>
  );
}

/**
 * The full 4-card bento grid. Used by both the slide and the public page.
 * Centers itself within its parent.
 */
export function PricingGrid({ compact = false, className = "" }) {
  return (
    <div className={cn("grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5", className)}>
      {PRICING_TIERS.map((t) => (
        <PricingTierCard key={t.key} tier={t} compact={compact} />
      ))}
    </div>
  );
}

/**
 * Icons for the footer note / callouts — kept here so they're importable
 * from one place if the public page wants to add trust signals.
 */
export { Sparkles, Shield, Crown, Target };
